import React, { forwardRef, useEffect, useState } from "react";
import styled, { css, withTheme } from "styled-components";
import DatePicker from "react-datepicker";
import moment from "moment";
import AlertUGNX from "../../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../../ToastsAndAlerts/constants";
import { useTranslation } from "react-i18next";

const StyledContainer = styled.div`
  .date-picker-wrapper {
    width: auto;
    flex: 1 1 auto !important;
    display: inline-block;
    padding: 0;
    border: 0;
  }
  .react-datepicker-wrapper {
    width: auto;
    flex: 1 1 auto !important;
  }
  .react-datepicker__input-container {
    position: relative;
    display: inline-block;
    width: 100%;
  }
  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--keyboard-selected,
  .react-datepicker__day--selected,
  .react-datepicker__header,
  .react-datepicker__month-text--in-range,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__month-text--selected,
  .react-datepicker__quarter-text--in-range,
  .react-datepicker__quarter-text--in-selecting-range,
  .react-datepicker__quarter-text--keyboard-selected,
  .react-datepicker__quarter-text--selected,
  .react-datepicker__time-list-item--selected {
    background-color: ${(props) => props?.theme?.darkPurple};
  }

  .react-datepicker__time-container
    .react-datepicker__time
    .react-datepicker__time-box
    ul.react-datepicker__time-list
    li.react-datepicker__time-list-item--selected {
    background-color: ${(props) => props?.theme?.darkPurple};
  }

  .react-datepicker-time__header,
  .react-datepicker-year-header,
  .react-datepicker__current-month,
  .react-datepicker__day-name,
  .react-datepicker__navigation--next,
  .react-datepicker__navigation--previous,
  .react-datepicker__time-name {
    color: ${(props) => props?.theme?.white};
  }

  .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle {
    border-bottom: none;
    border-top-color: ${(props) => props?.theme?.white};
  }

  .react-datepicker-popper[data-placement^="bottom"]
    .react-datepicker__triangle {
    border-top: none;
    border-bottom-color: ${(props) => props?.theme?.white};
  }
`;

const StyledInputTimePicker = styled.div`
  border: 0;
  border-radius: 0;
  background-color: ${(props) => props?.theme?.white};
  color: ${(props) => props?.theme?.fg};
  padding: 0.375rem 2.8rem 0.375rem
    ${(props) => (props.showBefore ? "2.8rem" : ".375rem")};
  margin-right: 20px;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
  border-bottom: 2px solid ${(props) => props?.theme?.dustyGray};
  min-width: ${(props) => (props.showBefore ? "250px" : "150px")};
  height: calc(1.5em + 0.75rem + 2px);
  width: 100%;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;

  &:hover {
    ${(props) =>
      !props.readOnly &&
      css`
        cursor: pointer;
        background-color: ${(props) => props?.theme?.whiteSmoke};
        border-bottom: 2px solid ${(props) => props.theme?.darkPurple} !important;
      `}
  }

  input {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    border: 0;
    border-radius: 0;
    background-color: transparent;

    &:focus {
      outline: none;
    }
  }
`;

const StyledInputIcon = styled.i`
  font-size: 1.1rem;
  position: absolute;
  top: 10px;
  right: ${(props) => (props.showBefore ? "auto" : "17px")};
  left: ${(props) => (props.showBefore ? "17px" : "auto")};
  ${(props) => (props.showBefore ? `color: ${props?.theme?.darkPurple}` : "")}
`;

const StyledTitle = styled.h6`
  margin-bottom: 0.25rem;
  font-weight: 700;
  line-height: 1.2;
`;

const InputTimePicker = forwardRef(
  (
    {
      onClick,
      className,
      showBefore,
      realDateValue,
      onValueChange,
      tempValue,
      readOnly,
      testid,
    },
    _ref
  ) => (
    <>
      <StyledInputTimePicker
        onClick={onClick}
        showBefore={showBefore}
        data-testid={testid || "inputTimePickerId"}
        readOnly={readOnly}
      >
        <input
          data-testid="input-time-picker"
          value={tempValue || realDateValue || ""}
          readOnly={readOnly}
          onChange={(e) => onValueChange(e.target.value)}
        />
        <StyledInputIcon className={className} />
      </StyledInputTimePicker>
    </>
  )
);

const time12Regex = new RegExp("^(0?[1-9]|1[012])(:[0-5]\\d) [AP][M]$");
const time24Regex = new RegExp("^(0?[1-9]|1[024])(:[0-5]\\d)$");

const TimePicker = (props) => {
  const {
    id,
    placeholder,
    onChange,
    value,
    readOnly,
    displayTitle = true,
    onError,
    timeIntervals = 1,
    maxTime,
    title,
    datePickerTimeFormat = "h:mm aa",
    datePickerInputTimeFormat = "hh:mm A",
  } = props;

  const { t } = useTranslation();

  const ref = React.createRef();

  const [selectedValue, setSelectedValue] = useState("");
  const [tempValue, setTempValue] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    onError(error);
  }, [error, onError]);

  const validateTime = (time, date) => {
    if (!time) {
      setError(true);
    }

    const timeValue = moment(time).format("HH:mm").split(":");

    const dateObj = moment(date);
    dateObj.set({
      h: timeValue[0],
      m: timeValue[1],
    });

    setError(dateObj.isAfter(moment(date)));
  };

  useEffect(() => {
    if (maxTime && selectedValue) {
      validateTime(selectedValue, maxTime);
    }
  }, [maxTime, selectedValue]);

  const handleTimeChange = (date) => {
    if (date !== "") {
      onChange(date);
      setSelectedValue(date);
      setError(false);
      setTempValue(null);
    }
  };

  const handleInputTimeValueChange = (value) => {
    let cleanValue = value.trim().toUpperCase();

    if (cleanValue.includes("AM") && !cleanValue.includes(" AM")) {
      cleanValue = cleanValue.replace("AM", " AM");
    }

    if (cleanValue.includes("PM") && !cleanValue.includes(" PM")) {
      cleanValue = cleanValue.replace("PM", " PM");
    }

    if (time12Regex.test(cleanValue) || time24Regex.test(cleanValue)) {
      const test = moment(cleanValue, "LT").toDate();
      onChange(test);
      setSelectedValue(test);
      setTempValue(null);
      setError(false);
    } else {
      setSelectedValue(null);
      setTempValue(cleanValue);
      setError(true);
    }
  };

  return (
    <StyledContainer data-testid="time-picker">
      {displayTitle && <StyledTitle>{title || t("appText.time")}</StyledTitle>}
      <div id={id}>
        <DatePicker
          placeholder={placeholder}
          readOnly={readOnly}
          selected={selectedValue}
          onChange={(date) => handleTimeChange(date)}
          maxTime={maxTime}
          minTime={moment(maxTime).startOf("day").toDate()}
          showTimeSelect
          timeFormat={datePickerTimeFormat}
          showTimeSelectOnly
          timeIntervals={timeIntervals}
          timeCaption={title || t("appText.time")}
          customInput={
            <InputTimePicker
              className={"far fa-clock"}
              testid={props["data-testid"]}
              ref={ref}
              readOnly={readOnly}
              onValueChange={(value) => handleInputTimeValueChange(value)}
              realDateValue={
                selectedValue
                  ? moment(selectedValue).format(datePickerInputTimeFormat)
                  : null
              }
              tempValue={tempValue}
            />
          }
        />

        {error && (
          <AlertUGNX data-testid="alert-invalid-time" type={AlertType.DANGER}>
            {t("errorText.invalidTime")}
          </AlertUGNX>
        )}
      </div>
    </StyledContainer>
  );
};

export default withTheme(TimePicker);
